// ═══════════════════════════════════════════════════════════
// uploadImage — compress image on client, upload to Firebase Storage
// Returns download URL (string) or throws on error
// ═══════════════════════════════════════════════════════════
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';

const MAX_DIMENSION = 800; // px
const MAX_SIZE_BYTES = 1 * 1024 * 1024; // 1MB output limit
const JPEG_QUALITY = 0.82;

/**
 * Compress an image File via canvas, then upload to Firebase Storage.
 * @param {File} file - original image file
 * @param {string} path - storage path, e.g. "tenants/abc/avatars/xyz.jpg"
 * @param {object} [opts] - optional overrides
 * @param {number} [opts.maxDimension] - max width/height in px
 * @param {number} [opts.maxSizeBytes] - max output size in bytes
 * @returns {Promise<string>} download URL
 */
export async function uploadImage(file, path, opts) {
  const compressed = await compressImage(file, opts);
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, compressed, { contentType: 'image/jpeg' });
  return getDownloadURL(storageRef);
}

/**
 * Upload any file (PDF, doc, etc.) to Firebase Storage without compression.
 * @param {File} file
 * @param {string} path
 * @returns {Promise<string>} download URL
 */
export async function uploadFile(file, path) {
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, { contentType: file.type });
  return getDownloadURL(storageRef);
}

/**
 * Compress image to maxDim × maxDim and maxSize via canvas.
 * @param {File} file
 * @param {object} [opts]
 * @returns {Promise<Blob>}
 */
function compressImage(file, opts) {
  const maxDim = opts?.maxDimension || MAX_DIMENSION;
  const maxSize = opts?.maxSizeBytes || MAX_SIZE_BYTES;
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width > height) {
          height = Math.round((height / width) * maxDim);
          width = maxDim;
        } else {
          width = Math.round((width / height) * maxDim);
          height = maxDim;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error('Canvas toBlob failed'));
          if (blob.size > maxSize) {
            // Second pass with lower quality
            canvas.toBlob(
              (blob2) => {
                if (!blob2) return reject(new Error('Canvas toBlob failed'));
                if (blob2.size > maxSize) return reject(new Error(`Image too large (${Math.round(blob2.size / 1024)} KB). Max: ${Math.round(maxSize / 1024)} KB`));
                resolve(blob2);
              },
              'image/jpeg', 0.5
            );
          } else {
            resolve(blob);
          }
        },
        'image/jpeg', JPEG_QUALITY
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
    img.src = url;
  });
}
